import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import axios from 'axios';

import { fetchCsrfToken } from '~/api/csrf';
import AuthenticationStore from '~/auth/authenticationStore';
import {
  refreshAccessToken,
  shouldRefreshAccessToken,
} from '~/auth/refreshAccessToken';
import { with401Retry } from '~/auth/with401Retry';
import { getAxiosErrorMessage } from '~/common/errors/getAxiosErrorMessage';
import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { showToast } from '~/eds/src/components/Toast/showToast';
import { LOGIN_CONSTANTS } from '~/features/login/constants';
import { DEFAULT_LANGUAGE, LANGUAGE_KEY, LOCALE_KEY } from '~/i18n/constants';
import { StorageService } from '~/services/StorageService/StorageService';

interface ServiceRequestConfigBase extends AxiosRequestConfig {
  showErrorToast?:
    | boolean
    | string
    | { message?: string; autoDismiss?: boolean; skipStatuses?: number[] };
  showSuccessToast?: boolean | string;
}

/**
 * Config that returns unwrapped data (default behavior).
 */
export interface ServiceRequestConfig extends ServiceRequestConfigBase {
  fullResponse?: false;
}

/**
 * Config that returns the full AxiosResponse.
 */
interface FullResponseRequestConfig extends ServiceRequestConfigBase {
  fullResponse: true;
}

export interface ServiceConfiguration {
  url: string;
  refresh_token?: boolean;
  shouldRetryOn401?: boolean;
  errorHandler?: (error: AxiosError<any>) => unknown;
}

/**
 * Overloaded method signature for methods without data (get, remove).
 */
interface ServiceMethod {
  <T = any>(
    url: string,
    config: FullResponseRequestConfig,
  ): Promise<AxiosResponse<T>>;
  <T = any>(url: string, config?: ServiceRequestConfig): Promise<T>;
}

/**
 * Overloaded method signature for methods with data (post, put, patch).
 */
interface ServiceMethodWithData {
  <T = any>(
    url: string,
    data: any,
    config: FullResponseRequestConfig,
  ): Promise<AxiosResponse<T>>;
  <T = any>(url: string, data?: any, config?: ServiceRequestConfig): Promise<T>;
}

/**
 * Service interface that returns data directly (not wrapped in AxiosResponse).
 * The response interceptor below extracts response.data automatically.
 * Pass { fullResponse: true } to get the full AxiosResponse instead.
 */
export interface Service {
  get: ServiceMethod;
  post: ServiceMethodWithData;
  put: ServiceMethodWithData;
  patch: ServiceMethodWithData;
  remove: ServiceMethod;
  concurrentRequests<T = any>(requests: Promise<any>[]): Promise<T[]>;
}

const getErrorMessage = (
  error: AxiosError,
  config: ServiceRequestConfigBase,
) => {
  if (typeof config.showErrorToast === 'string') {
    return config.showErrorToast;
  }
  if (
    typeof config.showErrorToast === 'object' &&
    config.showErrorToast?.message
  ) {
    return config.showErrorToast.message;
  }
  return getAxiosErrorMessage(error);
};

const defaultErrorHandler = (error: AxiosError) => {
  const config = error.config as ServiceRequestConfigBase | undefined;
  if (config?.showErrorToast) {
    const message = getErrorMessage(error, config);
    const toastConfig =
      typeof config.showErrorToast === 'object' ? config.showErrorToast : {};
    const skipStatus =
      error.response?.status !== undefined &&
      toastConfig.skipStatuses?.includes(error.response.status);

    // Don't show toast for cancelled requests, 401s (handled elsewhere), or
    // statuses the caller has explicitly opted out of.
    if (
      !axios.isCancel(error) &&
      error.response?.status !== 401 &&
      !skipStatus
    ) {
      showToast({
        status: 'danger',
        message,
        autoDismiss: toastConfig.autoDismiss,
      });
    }
  }

  return Promise.reject(error);
};

/**
 * Creates a service abstraction around the AxiosInstance. It currently only exposes common axios HTTP methods, but this can be expanded in the future.
 */
export default function createService(
  configuration: ServiceConfiguration,
): Service {
  const instance = axios.create({
    baseURL: configuration.url,
    timeout: 120000,
    allowAbsoluteUrls: false,
    withCredentials: getEnvVariable('REACT_APP_CORS_REQUESTS') === 'true',
  });

  const AS = AuthenticationStore();

  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // do something before request is sent
      config.headers = {
        'Accept-Language':
          StorageService.getItem(LOCALE_KEY) ||
          StorageService.getItem(LANGUAGE_KEY) ||
          DEFAULT_LANGUAGE,
        'User-Timezone':
          new Intl.DateTimeFormat().resolvedOptions().timeZone ||
          'America/Vancouver',
        ...config.headers,
      } as unknown as InternalAxiosRequestConfig['headers'];

      // Read fresh each request — `createService` runs once per service, so a
      // value captured here would freeze at boot (before login) and never
      // notice a later login, permanently skipping the CSRF fetch below.
      const isLoggedIn = AS.isLoggedIn();

      // Fetch and set up CSRF token
      if (isLoggedIn && !AS.getCsrf()) {
        await fetchCsrfToken();
      }

      if (AS.getCsrf()) {
        config.headers[LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN] = AS.getCsrf();
      }

      if (isLoggedIn && shouldRefreshAccessToken()) {
        await refreshAccessToken();
      }

      if (!AS.getAccessToken() || !AS.getRefreshToken()) {
        // for httponly support. No longer need access token or refresh token
        return config;
      }

      // Only set auth token if it exists and caller has not set an explicit Authorization header
      if (
        !config.headers.Authorization &&
        (AS.getAccessToken() || AS.getRefreshToken())
      ) {
        config.headers.Authorization = configuration.refresh_token
          ? `Bearer ${AS.getRefreshToken()}`
          : `Bearer ${AS.getAccessToken()}`;
      }

      return config;
    },
  );

  instance.interceptors.response.use((response: AxiosResponse) => {
    const config = response.config as ServiceRequestConfigBase & {
      fullResponse?: boolean;
    };
    const { data } = response;

    if (config.showSuccessToast) {
      const message =
        typeof config.showSuccessToast === 'string'
          ? config.showSuccessToast
          : data.message;

      showToast({ status: 'success', message });
    }

    return config.fullResponse ? response : data;
  }, configuration.errorHandler || defaultErrorHandler);

  if (configuration.shouldRetryOn401) {
    return {
      get: with401Retry(instance.get.bind(instance)),
      post: with401Retry(instance.post.bind(instance)),
      remove: with401Retry(instance.delete.bind(instance)),
      put: with401Retry(instance.put.bind(instance)),
      patch: with401Retry(instance.patch.bind(instance)),
      concurrentRequests: with401Retry(axios.all.bind(axios)),
    };
  }

  return {
    get: instance.get,
    post: instance.post,
    remove: instance.delete,
    put: instance.put,
    patch: instance.patch,
    concurrentRequests: axios.all,
  };
}
